/* eslint-disable @typescript-eslint/no-explicit-any */
import { memo, useEffect, useState, useMemo } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
  TableBody,
  TableRow,
} from '@mui/material';
import CustomTableCell from 'components/cmn/CustomTableCell';
import { ICmnTableBodyProps } from 'interface/cmn/commonInfo';
import { AccountsContent } from 'interface/project/projectInfo';
import { getComparatorAny, stableSort } from 'libs/mindUtils';
import { postAdminRoleOneMapping } from 'store/project/projectSlice';
import { useAppDispatch } from 'store';
import { closeBackdrop, openBackdrop } from 'store/backdrop/backdropSlice';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useNavigate } from 'react-router-dom';

const initialRoles = (rows: AccountsContent[]) =>
  rows.reduce(
    (acc, row) => {
      acc[row.id] = row.roles[0]?.id || 12;
      return acc;
    },
    {} as { [key: string]: number },
  );
interface IAccountManageTableBodyProps extends ICmnTableBodyProps<AccountsContent> {
  mutate: any;
}
function AccountManageTableBody({ loading, rows, mutate }: IAccountManageTableBodyProps): JSX.Element {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const [originalRoles, setOriginalRoles] = useState<{ [key: string]: number }>({});
  const [updatedRoles, setUpdatedRoles] = useState<{ [key: string]: number }>({});

  useEffect(() => {
    if (!loading) {
      const newRoles = initialRoles(rows);
      setOriginalRoles(newRoles);
      setUpdatedRoles(newRoles);
    }
  }, [loading, rows]);

  const changedRoles = useMemo(() => {
    const changes: { [key: string]: number } = {};
    Object.keys(updatedRoles).forEach(id => {
      if (originalRoles[id] !== updatedRoles[id]) {
        changes[id] = updatedRoles[id];
      }
    });
    return changes;
  }, [originalRoles, updatedRoles]);

  const hasChanges = useMemo(() => Object.keys(changedRoles).length > 0, [changedRoles]);

  const handleSave = () => {
    const changedRoleData = Object.entries(changedRoles).map(([adminId, roleId]) => ({
      adminId,
      roleId,
    }));
    postMultipleAdminRoleMappings(changedRoleData)
      .then(result => {
        console.log('result', result);
      })
      .catch(error => {
        console.log('error', error);
      });
  };

  const postMultipleAdminRoleMappings = async (updates: { adminId: string; roleId: number }[]) => {
    dispatch(openBackdrop({}));
    try {
      const promises = updates.map(update => dispatch(postAdminRoleOneMapping(update)));
      const results = await Promise.all(promises);
      return results;
    } catch (error) {
      console.log(error);
      throw error;
    } finally {
      mutate();
      dispatch(closeBackdrop());
    }
  };
  const handleCancel = () => {
    setUpdatedRoles(originalRoles);
  };
  const handleRoleChange = (id: string, newRoleId: number) => {
    setUpdatedRoles(prev => ({ ...prev, [id]: newRoleId }));
  };
  const handleAccountInfo = (row: AccountsContent) => {
    navigate(`/admin/member-management/${row.id}`, {
      state: {
        name: row.name,
      },
    });
  };
  return (
    <>
      {!loading && rows.length === 0 && (
        <TableBody>
          <TableRow>
            <CustomTableCell align="center" colSpan={7} sx={{ border: 'none' }}>
              조회된 데이터가 없습니다.
            </CustomTableCell>
          </TableRow>
        </TableBody>
      )}
      {!loading && rows.length !== 0 && (
        <>
          <TableBody>
            {stableSort(rows, getComparatorAny('desc', 'id')).map((row, index) => {
              const labelId = `table-checkbox-${index}`;
              return (
                <TableRow hover tabIndex={-1} key={row.id} id={labelId}>
                  <CustomTableCell align="center">{row.email}</CustomTableCell>
                  <CustomTableCell align="center">{row.name}</CustomTableCell>
                  <CustomTableCell align="center">{row.mobile}</CustomTableCell>
                  <CustomTableCell align="center">{row.compName || '-'}</CustomTableCell>
                  <CustomTableCell align="center">{row.compRole || '-'}</CustomTableCell>
                  <CustomTableCell align="center">
                    {Object.keys(updatedRoles).length > 0 && (
                      <FormControl>
                        <RadioGroup
                          row
                          value={updatedRoles[row.id] ?? 12}
                          aria-labelledby="roleCode"
                          name="row-radio-buttons-group"
                          onChange={e => handleRoleChange(row.id, parseInt(e.target.value, 10))}
                        >
                          <FormControlLabel value={12} control={<Radio />} label="Guest" />
                          <FormControlLabel value={11} control={<Radio />} label="강사회원" />
                          <FormControlLabel value={10} control={<Radio />} label="관리자" />
                        </RadioGroup>
                      </FormControl>
                    )}
                  </CustomTableCell>
                  <CustomTableCell align="center">
                    <IconButton onClick={() => handleAccountInfo(row)} sx={{ width: 32 }}>
                      <OpenInNewIcon />
                    </IconButton>
                  </CustomTableCell>
                </TableRow>
              );
            })}
          </TableBody>
          {hasChanges && (
            <Box display="flex" justifyContent="center" marginTop={2}>
              <Button variant="contained" color="primary" onClick={handleSave} sx={{ marginRight: 2 }}>
                작업저장
              </Button>
              <Button variant="outlined" color="secondary" onClick={handleCancel}>
                작업취소
              </Button>
            </Box>
          )}
        </>
      )}
    </>
  );
}

export default memo(AccountManageTableBody);
